// The self-update ROLLOUT state machine — one hop per row, `decision` on the
// controller's own `runner.update_*` chain and `UpdateBoxDecision` on the BOX's
// own testimony (`apps/worker/src/fleet/box-event-sink.ts`), plus the release
// ledger (`runner.release_recorded` / `runner.autoroll_started` /
// `runner.release_pruned`). Split out of `dispatch.ts` (rule 9 of the task
// brief): these 19 kinds share one payload shape (`rolloutAuditEvent`,
// `apps/worker/src/fleet/update-rollout.ts`) and one render helper, and folding
// them into the box-identity file would have pushed it well past 800 lines.
//
// ─── MOST OF THIS CHAIN IS `'omit'`, ON PURPOSE ──────────────────────────────
//
// A rollout publishes to every box in a wave and most confirm cleanly — a fleet
// of hundreds would flood the console with a routine positive per box per
// release. The lines that stay are the ones an operator cannot get anywhere
// else: a box FAILING or REFUSING a build, a release being HALTED or a box
// ROLLED BACK off a condemned one, a box's own disk-level SWAP ABORTING or
// BOOT-ROLLING-BACK, and the release-ledger housekeeping (recorded / autoroll
// started / pruned). Every omission below says why, next to the kind.

import type {
  TerminalFailRenderContext,
  TerminalFailRendered,
  TerminalRenderContext,
  TerminalRendered,
} from '../../../wire/terminal';
import { attemptParams, terminalRendererGroup } from '../../../wire/terminal';
import { AuditEventKind } from '../../audit';
import { boxSubject, num, rec, str } from './dispatch-payload';

/** `runner.update_failed` / `runner.update_refused` share this input shape. */
function updateBoxSubject(ctx: TerminalFailRenderContext) {
  const input = rec(ctx.row.input);
  const runnerId = str(input, 'runnerId');
  return { input, runnerId, subject: boxSubject(runnerId) };
}

const runnerUpdateFailed = {
  level: 'fail' as const,
  actor: 'system' as const,
  render(ctx: TerminalFailRenderContext): TerminalFailRendered | null {
    const { input, runnerId, subject } = updateBoxSubject(ctx);
    if (subject === null || runnerId === undefined) return null;
    const version = str(input, 'version') ?? 'unknown';
    const reason = str(input, 'reason') ?? 'unknown';
    return {
      line: {
        key: 'terminal.runner.update_failed',
        params: { ...attemptParams(ctx.attempt), version, reason },
      },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

/**
 * Rendered `warn`, not `fail`: the release is explicitly NOT convicted by this
 * row (`rolloutAuditEvent`'s `output.countedAgainstRelease: false`) — the box's
 * own configuration refused it, which is a fault an operator fixes on the box,
 * never a build to roll back.
 */
const runnerUpdateRefused = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const version = str(input, 'version') ?? 'unknown';
    const reason = str(input, 'reason') ?? 'unknown';
    return {
      line: { key: 'terminal.runner.update_refused', params: { version, reason } },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerExcusalStreak = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const versions = num(input, 'versions') ?? 0;
    const bound = num(input, 'bound') ?? 0;
    return {
      line: { key: 'terminal.runner.excusal_streak', params: { versions, bound } },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerUpdateHalted = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const version = str(input, 'version');
    const channel = str(input, 'channel');
    if (version === undefined || channel === undefined) return null;
    const failed = num(input, 'failed') ?? 0;
    const confirmed = num(input, 'confirmed') ?? 0;
    return {
      line: {
        key: 'terminal.runner.update_halted',
        params: { version, channel, failed, confirmed },
      },
      subject: { kind: 'decision', id: `${channel}:${version}`, label: version },
      refs: {},
    };
  },
};

const runnerUpdateRolledBack = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const fromVersion = str(input, 'fromVersion') ?? 'unknown';
    const toVersion = str(input, 'toVersion') ?? 'unknown';
    return {
      line: {
        key: 'terminal.runner.update_rolled_back',
        params: { fromVersion, toVersion },
      },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerUpdateRetargeted = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const channel = str(input, 'channel');
    const toVersion = str(input, 'toVersion');
    if (channel === undefined || toVersion === undefined) return null;
    const fromVersion = str(input, 'fromVersion') ?? 'unknown';
    return {
      line: {
        key: 'terminal.runner.update_retargeted',
        params: { channel, fromVersion, toVersion },
      },
      subject: { kind: 'decision', id: `${channel}:${toVersion}`, label: toVersion },
      refs: {},
    };
  },
};

const runnerUpdateSwapAborted = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    return {
      line: { key: 'terminal.runner.update_swap_aborted', params: {} },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerUpdateBootRolledBack = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    return {
      line: { key: 'terminal.runner.update_boot_rolled_back', params: {} },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

const runnerRolloutResumed = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const channel = str(input, 'channel');
    const version = str(input, 'version');
    if (channel === undefined || version === undefined) return null;
    const round = num(input, 'round') ?? 0;
    return {
      line: { key: 'terminal.runner.rollout_resumed', params: { channel, version, round } },
      subject: { kind: 'decision', id: `${channel}:${version}`, label: version },
      refs: {},
    };
  },
};

const runnerReleaseRecorded = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const version = str(input, 'version');
    if (version === undefined) return null;
    const channel = str(input, 'channel') ?? 'unknown';
    return {
      line: { key: 'terminal.runner.release_recorded', params: { version, channel } },
      subject: { kind: 'decision', id: version, label: version },
      refs: {},
    };
  },
};

const runnerAutorollStarted = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const accountId = str(input, 'accountId');
    const version = str(input, 'version');
    if (accountId === undefined || version === undefined) return null;
    return {
      line: { key: 'terminal.runner.autoroll_started', params: { version } },
      subject: { kind: 'account', id: accountId, label: accountId },
      refs: {},
    };
  },
};

const runnerReleasePruned = {
  level: 'info' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    // `output` is the lane's outcome verbatim (`apps/worker/src/cron/prune-runner-releases.ts`
    // `PruneRunnerReleasesAudit`). `marked` is the pass's PRE-DELETE intent row, written
    // before its `pruned_at` marks; the same pass's closing row (`pruned` /
    // `delete_failed` / `mark_failed` / `refused`) follows it, so it gets no line of its
    // own. On a closing row the destroyed versions are `deleted` — on `pruned` and on
    // `delete_failed` alike, where the bytes were gone before the pass stopped — and
    // absent where nothing was deleted, which reads as 0. Until #7213 this read
    // `output.pruned`, a field the row never had, so every line said 0.
    const output = rec(ctx.row.output);
    if (output?.['outcome'] === 'marked') return null;
    const deleted = output?.['deleted'];
    const count = Array.isArray(deleted) ? deleted.length : 0;
    return {
      line: { key: 'terminal.runner.release_pruned', params: { count } },
      // `id` is the row's own session, not a constant string — a hardcoded id
      // would collapse every account's release-prune row into one subject,
      // against `wire/terminal.ts`'s `TerminalSubject` contract (round-2
      // review). The human-readable label is unaffected.
      subject: { kind: 'session', id: ctx.row.sessionId, label: 'release prune' },
      refs: {},
    };
  },
};

const runnerBootRelease = {
  level: 'warn' as const,
  actor: 'system' as const,
  render(ctx: TerminalRenderContext): TerminalRendered | null {
    const input = rec(ctx.row.input);
    const runnerId = str(input, 'runnerId');
    const subject = boxSubject(runnerId);
    if (subject === null || runnerId === undefined) return null;
    const releasedTaskIds = input?.['releasedTaskIds'];
    const released = Array.isArray(releasedTaskIds) ? releasedTaskIds.length : 0;
    return {
      line: { key: 'terminal.runner.boot_release', params: { released } },
      subject,
      refs: { boxId: runnerId },
    };
  },
};

export const TERMINAL_RENDERERS_DISPATCH_ROLLOUT = terminalRendererGroup({
  [AuditEventKind.RunnerUpdatePublished]: 'omit', // per-box intent; every wave, floods a fleet-sized console
  [AuditEventKind.RunnerUpdateConfirmed]: 'omit', // routine positive confirmation, the common case
  [AuditEventKind.RunnerUpdateFailed]: runnerUpdateFailed,
  [AuditEventKind.RunnerUpdateRefused]: runnerUpdateRefused,
  [AuditEventKind.RunnerExcusalStreak]: runnerExcusalStreak,
  [AuditEventKind.RunnerUpdateHalted]: runnerUpdateHalted,
  [AuditEventKind.RunnerUpdateRolledBack]: runnerUpdateRolledBack,
  [AuditEventKind.RunnerUpdateRetargeted]: runnerUpdateRetargeted,
  [AuditEventKind.RunnerUpdateRetargetHeld]: 'omit', // advisory: the pass kept its target; nothing on the fleet moved
  [AuditEventKind.RunnerUpdateHandedOff]: 'omit', // pass bookkeeping: the box's own verified/boot_rolled_back rows carry the install it hands off
  [AuditEventKind.RunnerUpdatePulled]: 'omit', // pre-work intent only, box-local; verified/failed cover the outcome
  [AuditEventKind.RunnerUpdateSwapped]: 'omit', // routine; verified/boot_rolled_back cover the outcome
  [AuditEventKind.RunnerUpdateSwapAborted]: runnerUpdateSwapAborted,
  [AuditEventKind.RunnerUpdateVerified]: 'omit', // routine positive confirmation, pull-side counterpart of confirmed
  [AuditEventKind.RunnerUpdateBootRolledBack]: runnerUpdateBootRolledBack,
  [AuditEventKind.RunnerUpdateDeferred]: 'omit', // routine: a box waiting out a live run before swapping
  [AuditEventKind.RunnerUpdateDeferredSettled]: 'omit', // routine: the wait above ending
  [AuditEventKind.RunnerUpdateFetchFailed]: 'omit', // the controller's update_failed row names the grade
  [AuditEventKind.RunnerRolloutResumed]: runnerRolloutResumed,
  [AuditEventKind.RunnerReleaseRecorded]: runnerReleaseRecorded,
  [AuditEventKind.RunnerAutorollStarted]: runnerAutorollStarted,
  [AuditEventKind.RunnerReleasePruned]: runnerReleasePruned,
  [AuditEventKind.RunnerBootRelease]: runnerBootRelease,
});
